import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiIcon,
  EuiLink,
  EuiPanel,
  EuiSpacer,
  EuiText,
} from "@inscopix/ideas-eui";
import { UserAvatar } from "components/UserAvatar/UserAvatar";
import { useState } from "react";
import { useUserContext } from "providers/UserProvider/UserProvider";
import { useRouteMapContext } from "providers/RouteMapProvider/RouteMapProvider";
import { RegionBadge } from "components/RegionBadge/RegionBadge";
import { SupportLinks } from "components/SupportLinks/SupportLinks";
import { getEnvVar } from "ideas.env";

const PageDashboardSidebarUser = () => {
  const routeMap = useRouteMapContext((s) => s.routeMap);
  const currentUser = useUserContext((s) => s.currentUser);

  return (
    <EuiFlexGroup alignItems="center" gutterSize="xs">
      <EuiFlexItem grow={false}>
        <UserAvatar userId={currentUser.id} size="xl" />
      </EuiFlexItem>
      <EuiFlexItem>
        <EuiFlexGroup direction="column" gutterSize={"none"}>
          <EuiFlexItem>
            <EuiText title={`${currentUser.firstName} ${currentUser.lastName}`}>
              <h4>
                {currentUser.firstName} {currentUser.lastName}
              </h4>
            </EuiText>
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiText
              color="subdued"
              size="xs"
              title={currentUser.email}
              style={{
                maxWidth: "180px",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {currentUser.email}
            </EuiText>
          </EuiFlexItem>
          <EuiSpacer size="xs" />
          <EuiFlexItem>
            <EuiLink to={routeMap["SETTINGS"].path}>Profile</EuiLink>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexItem>
    </EuiFlexGroup>
  );
};

const PageDashBoardSideBarAbout = () => {
  return (
    <EuiFlexGroup direction="column" gutterSize={"s"}>
      <EuiFlexItem>
        <EuiText>
          <h3>About</h3>
        </EuiText>
      </EuiFlexItem>
      <EuiFlexItem>
        <EuiFlexGroup direction="column" gutterSize={"s"}>
          <EuiFlexItem>
            <EuiText>
              Version: <strong>25.3.4</strong>
            </EuiText>
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiText>
              Region:{" "}
              <strong>
                {getEnvVar("CURRENT_REGION").longName}{" "}
                {`(${getEnvVar("CURRENT_REGION").shortName})`}
              </strong>
            </EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlexItem>
    </EuiFlexGroup>
  );
};

const PageDashBoardSideBarSupport = () => {
  return (
    <EuiFlexGroup direction="column" gutterSize={"s"}>
      <EuiFlexItem>
        <EuiText>
          <h3>Support</h3>
        </EuiText>
      </EuiFlexItem>
      <SupportLinks />
    </EuiFlexGroup>
  );
};

const PageDashBoardSideBarOrganizations = () => {
  const routeMap = useRouteMapContext((s) => s.routeMap);
  const { tenants, externalTenants, personalSpace } = useUserContext((s) => ({
    tenants: s.tenants,
    externalTenants: s.externalTenants,
    personalSpace: s.personalSpace,
  }));
  const [showAll, setShowAll] = useState(false);
  const SHOWCOUNT = 5;

  const allTenants = [...tenants, ...externalTenants];

  const tenantsToShow = showAll ? allTenants : allTenants.slice(0, SHOWCOUNT);

  if (personalSpace === undefined && tenantsToShow.length === 0) {
    return null;
  }

  return (
    <div>
      <EuiFlexGroup direction="column" gutterSize="s">
        <EuiFlexItem>
          {personalSpace && (
            <>
              <EuiPanel paddingSize="xs" color="subdued">
                <EuiFlexGroup
                  key={`${
                    "region" in personalSpace && personalSpace.region
                      ? personalSpace.region.KEY
                      : "current"
                  }-${String(personalSpace.id)}`}
                  alignItems={"center"}
                  gutterSize="s"
                >
                  <EuiFlexItem grow={false}>
                    <EuiIcon type={"spaces"} />
                  </EuiFlexItem>
                  <EuiFlexItem>
                    <EuiText>
                      {"region" in personalSpace && personalSpace.region ? (
                        <EuiLink
                          href={`${personalSpace.region.URL}${
                            routeMap["ORGANIZATION"].dynamicPath({
                              tenantKey: personalSpace.key,
                            }).path
                          }`}
                          target="_blank"
                        >
                          Personal Space
                        </EuiLink>
                      ) : (
                        <EuiLink
                          to={
                            routeMap["ORGANIZATION"].dynamicPath({
                              tenantKey: personalSpace.key,
                            }).path
                          }
                        >
                          Personal Space
                        </EuiLink>
                      )}
                    </EuiText>
                  </EuiFlexItem>
                  {"region" in personalSpace && personalSpace.region && (
                    <EuiFlexItem grow={false}>
                      <RegionBadge
                        regionShortName={personalSpace.region.shortName}
                      />
                    </EuiFlexItem>
                  )}
                </EuiFlexGroup>
              </EuiPanel>
              <EuiSpacer size="s" />
            </>
          )}
        </EuiFlexItem>
        {tenantsToShow.length > 0 && (
          <>
            <EuiFlexItem>
              <EuiText>
                <h3>Organizations</h3>
              </EuiText>
            </EuiFlexItem>
            <EuiFlexItem>
              {tenantsToShow.map((tenant) => (
                <EuiPanel
                  key={`${
                    "region" in tenant ? tenant.region.KEY : "current"
                  }-${String(tenant.id)}`}
                  hasShadow={false}
                  hasBorder={true}
                  color="subdued"
                  paddingSize="xs"
                >
                  <EuiFlexGroup alignItems={"center"} gutterSize="s">
                    <EuiFlexItem>
                      <EuiText>
                        {"region" in tenant ? (
                          <EuiLink
                            href={`${tenant.region.URL}${
                              routeMap["ORGANIZATION"].dynamicPath({
                                tenantKey: tenant.key,
                              }).path
                            }`}
                            target="_blank"
                          >
                            {tenant.name}
                          </EuiLink>
                        ) : (
                          <EuiLink
                            to={
                              routeMap["ORGANIZATION"].dynamicPath({
                                tenantKey: tenant.key,
                              }).path
                            }
                          >
                            {tenant.name}
                          </EuiLink>
                        )}
                      </EuiText>
                    </EuiFlexItem>
                    {"region" in tenant && (
                      <EuiFlexItem grow={false}>
                        <RegionBadge
                          regionShortName={tenant.region.shortName}
                        />
                      </EuiFlexItem>
                    )}
                  </EuiFlexGroup>
                </EuiPanel>
              ))}
              {allTenants.length > SHOWCOUNT && (
                <>
                  <EuiSpacer size="s" />
                  <EuiLink onClick={() => setShowAll((prev) => !prev)}>
                    Show {showAll ? "less" : "more"}
                  </EuiLink>
                </>
              )}
            </EuiFlexItem>
          </>
        )}
      </EuiFlexGroup>
    </div>
  );
};

export const PageDashboardSideBar = () => {
  return (
    <EuiFlexGroup direction="column" gutterSize="xl">
      <EuiFlexItem>
        <PageDashboardSidebarUser />
      </EuiFlexItem>
      <EuiFlexItem>
        <PageDashBoardSideBarOrganizations />
      </EuiFlexItem>
      <EuiFlexItem>
        <PageDashBoardSideBarSupport />
      </EuiFlexItem>
      <EuiFlexItem>
        <PageDashBoardSideBarAbout />
      </EuiFlexItem>
    </EuiFlexGroup>
  );
};
